import {
  FileImage,
  Palette,
  FileBarChart2,
  Award,
  GraduationCap,
  Image,
  ChevronDown,
  Sparkles,
} from "lucide-react";
import {
  createImageGenerator,
  enhancePrompt,
  useGetImageGenerator,
  useGetTopImageGenerators,
} from "@packages/api/madlenAPI";
import { getEnvironment } from "@packages/utils";
import { CustomPromptInput } from "./CustomPromptInput";
import { useEffect, useState, useMemo, useCallback, memo } from "react";
import {
  ImageGeneratorResponseDto,
  WordAlternatives,
} from "@packages/api/madlenAPI.schemas";

import { useTranslation } from "react-i18next";

import { useMobile } from "@packages/hooks/useMobile";
import { trackEvent } from "@packages/hooks/useAnalytics";
import { useToast } from "@packages/contexts/ToastContext";

import { ImageGeneratorCreateRequestDtoGenerationType } from "@packages/api/madlenAPI.schemas";
import { updateLastUsedTools } from "@packages/store/userSlice";
import { useAppDispatch } from "@packages/store/hooks";

import { AnimatePresence } from "framer-motion";
import { motion } from "framer-motion";
import { CustomBubble } from "./CustomBubble";
import TemplateTypeSelector from "./TemplateTypeSelector";
import { EnhanceButton } from "@packages/components/enhance-button";
import { ThinkingBubble } from "@packages/components/thinking-bubble";

// Map for image generator types
export const IMAGE_GENERATOR_TYPES = {
  DEFAULT: {
    label: "image_generation_types.default",
    description: "image_generation_types.default_description",
    icon: Image,
    color: "indigo",
  },
  POSTER: {
    label: "image_generation_types.poster",
    description: "image_generation_types.poster_description",
    icon: FileImage,
    color: "blue",
  },
  LOGO: {
    label: "image_generation_types.logo",
    description: "image_generation_types.logo_description",
    icon: Palette,
    color: "emerald",
  },
  INFOGRAPHIC: {
    label: "image_generation_types.infographic",
    description: "image_generation_types.infographic_description",
    icon: FileBarChart2,
    color: "cyan",
  },
  BADGE: {
    label: "image_generation_types.badge",
    description: "image_generation_types.badge_description",
    icon: Award,
    color: "amber",
  },
  CERTIFICATE: {
    label: "image_generation_types.certificate",
    description: "image_generation_types.certificate_description",
    icon: GraduationCap,
    color: "purple",
  },
} as const;

export type ImageGeneratorImageType = keyof typeof IMAGE_GENERATOR_TYPES;

const convertToHighlightedWord = (wordAlt: WordAlternatives) => ({
  word: wordAlt.word || "",
  alternatives: wordAlt.alternatives || [],
});

// Generate Icon Component - Similar to EnhanceButton style
const GenerateIcon = memo<{
  isGenerating: boolean;
  hasPrompt: boolean;
  onGenerate: () => void;
}>(({ isGenerating, hasPrompt, onGenerate }) => {
  const { t } = useTranslation();

  return (
    <motion.div
      className="flex items-center gap-2 text-sm bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl rounded-2xl px-4 py-2.5 shadow-lg cursor-pointer border border-white/40 dark:border-gray-700/40"
      onClick={isGenerating || !hasPrompt ? undefined : onGenerate}
      data-testid="generate-images-button"
      whileHover={
        !isGenerating && hasPrompt
          ? {
              scale: 1.05,
              boxShadow: "0 10px 30px rgba(34,197,94,0.2)",
              transition: { duration: 0.2 },
            }
          : undefined
      }
      whileTap={!isGenerating && hasPrompt ? { scale: 0.95 } : undefined}
      style={{
        opacity: !hasPrompt ? 0.5 : 1,
        cursor: !hasPrompt || isGenerating ? "not-allowed" : "pointer",
      }}
    >
      <motion.div
        animate={
          isGenerating
            ? {
                rotate: 360,
                scale: [1, 1.2, 1],
              }
            : !hasPrompt
              ? {}
              : {
                  rotate: [0, 5, -5, 0],
                  scale: [1, 1.1, 1],
                }
        }
        transition={
          isGenerating
            ? {
                duration: 0.8,
                repeat: Infinity,
                ease: "linear",
              }
            : {
                duration: 2,
                repeat: Infinity,
                ease: "easeInOut",
              }
        }
      >
        {/* Colorful effect during generation */}
        <div className="relative">
          <Sparkles
            className={`w-4 h-4 ${
              isGenerating
                ? "text-transparent"
                : hasPrompt
                  ? "text-emerald-600 dark:text-emerald-400"
                  : "text-gray-400 dark:text-gray-500"
            }`}
          />
          {/* Colorful overlay during generation */}
          {isGenerating && (
            <motion.div
              className="absolute inset-0"
              animate={{
                background: [
                  "linear-gradient(45deg, #ef4444, #f97316)",
                  "linear-gradient(135deg, #f97316, #eab308)",
                  "linear-gradient(225deg, #eab308, #22c55e)",
                  "linear-gradient(315deg, #22c55e, #06b6d4)",
                  "linear-gradient(45deg, #06b6d4, #8b5cf6)",
                  "linear-gradient(135deg, #8b5cf6, #ef4444)",
                ],
              }}
              transition={{
                duration: 1.5,
                repeat: Infinity,
                ease: "linear",
              }}
              style={{
                WebkitBackgroundClip: "text",
                backgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              <Sparkles className="w-4 h-4" />
            </motion.div>
          )}
        </div>
      </motion.div>
      <span
        className={`font-medium ${
          isGenerating
            ? "text-transparent bg-gradient-to-r from-red-500 via-yellow-500 via-green-500 via-blue-500 to-purple-500 bg-clip-text animate-pulse"
            : hasPrompt
              ? "text-emerald-600 dark:text-emerald-400"
              : "text-gray-400 dark:text-gray-500"
        }`}
      >
        {isGenerating ? t("imagen.generating") : t("imagen.generate")}
      </span>
    </motion.div>
  );
});

GenerateIcon.displayName = "GenerateIcon";

const CreateImage = ({
  setSelectedImage,
  onScrollToGallery,
}: {
  setSelectedImage: (image: ImageGeneratorResponseDto) => void;
  onScrollToGallery?: () => void;
}) => {
  const { t } = useTranslation();
  const [prompt, setPrompt] = useState("");
  const [originalPrompt, setOriginalPrompt] = useState("");
  const [newId, setNewId] = useState<number | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);

  const isMobile = useMobile();
  const [highlightedWords, setHighlightedWords] = useState<WordAlternatives[]>(
    [],
  );
  const { addToast } = useToast();
  const dispatch = useAppDispatch();
  const [selectedTemplateType, setSelectedTemplateType] =
    useState<ImageGeneratorImageType>("DEFAULT");
  const [isEnhancing, setIsEnhancing] = useState(false);
  const [isEnhanced, setIsEnhanced] = useState(false);

  const [showPreview, setShowPreview] = useState(false);

  const handleTemplateTypeChange = useCallback(
    (type: ImageGeneratorImageType) => {
      setSelectedTemplateType(type);
    },
    [],
  );

  // Memoized value for hasPrompt
  const hasPrompt = useMemo(() => prompt.trim().length > 0, [prompt]);

  const { data: generatedImages } = useGetImageGenerator(newId!, {
    query: {
      enabled: !!newId,
      refetchInterval: (query) => {
        const exist =
          query.state.data?.images &&
          query.state.data?.images[0] &&
          query.state.data?.images[0].imageUrl;
        if (!exist) {
          console.log(
            "Resim henüz hazır değil, 3 saniye sonra tekrar deneyin...",
          );
          return 3000;
        }
        console.log("Resim hazır, yeniden sorgulama durduruldu");
        return false;
      },
    },
  });

  const { data: topImages } = useGetTopImageGenerators({
    size: 10,
  });

  useEffect(() => {
    if (
      generatedImages?.images &&
      generatedImages.images[0] &&
      generatedImages.images[0].wordAlternatives
    ) {
      setHighlightedWords(generatedImages.images[0].wordAlternatives);
    }
  }, [generatedImages]);

  useEffect(() => {
    if (generatedImages?.images && generatedImages.images[0]?.imageUrl) {
      console.log("Resim URL'si bulundu:", generatedImages.images[0].imageUrl);
      setIsGenerating(false);
      setShowPreview(true);
    }
  }, [generatedImages]);

  const { trackEvent: track } = trackEvent();

  const handleGenerateImages = useCallback(async () => {
    if (!prompt.trim()) {
      addToast({
        title: t("imagen.noPromptTitle"),
        description: t("imagen.noPromptDesc"),
        variant: "warning",
        duration: 3000,
      });
      return;
    }

    try {
      // Yeni görüntü oluşturmadan önce durumu sıfırlayalım
      setIsGenerating(true);
      setShowPreview(false);
      setNewId(null);

      console.log("Görüntü oluşturma isteği gönderiliyor...");

      const response = await createImageGenerator({
        prompt: prompt,
        isPublic: true,
        environment: getEnvironment(),
        generationType: ImageGeneratorCreateRequestDtoGenerationType.PROMPT,
        imageType: selectedTemplateType,
      });

      console.log("API yanıtı:", response);
      dispatch(updateLastUsedTools("images"));

      if (response && response.id) {
        console.log("Yeni ID alındı:", response.id);
        setNewId(response.id);
      } else {
        throw new Error("Görüntü oluşturma işleminden geçerli bir ID dönmedi");
      }
    } catch (error: any) {
      console.error("Görüntü oluşturma hatası:", error);
      setIsGenerating(false);
      if (
        error.response?.data.message ===
        "Content contains inappropriate language"
      ) {
        addToast({
          title: t("common.inappropriateContent.title"),
          description: t("common.inappropriateContent.description"),
          variant: "error",
          duration: 3000,
        });
      } else {
        addToast({
          title: t("common.error"),
          description: t("imagen.generateError"),
          variant: "error",
          duration: 3000,
        });
      }
    }

    track(
      {
        category: "ContentGeneration",
        action: "imagegen.generate",
        label: "Imagegen generation click",
        nonInteraction: false,
      },
      {
        location: "/imagegen/generate",
        element: "generate-button",
        imagegenPrompt: prompt,
      },
    );
  }, [prompt, selectedTemplateType, dispatch, addToast, t, track]);

  const handleEnhanceClick = useCallback(async () => {
    if (!prompt.trim()) {
      addToast({
        title: t("imagen.noPromptTitle"),
        description: t("imagen.noPromptDesc"),
        variant: "warning",
        duration: 3000,
      });
      return;
    }

    // If already enhanced, return to original prompt
    if (isEnhanced) {
      setPrompt(originalPrompt);
      setIsEnhanced(false);
      return;
    }

    // Force blur any active textarea to exit editing mode
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }

    // Small delay to ensure blur completes before proceeding
    await new Promise((resolve) => setTimeout(resolve, 10));

    setIsEnhancing(true);
    setOriginalPrompt(prompt);

    try {
      const enhancedPromptRequest = await enhancePrompt({
        prompt: prompt,
        toolType: "IMAGE_GENERATOR",
        imageType: selectedTemplateType,
      });

      const enhancedPrompt = enhancedPromptRequest.result;
      if (enhancedPrompt) {
        setPrompt(enhancedPrompt);
        setIsEnhanced(true);
      } else {
        throw new Error("No enhanced prompt returned");
      }
    } catch (error) {
      console.error("Error enhancing prompt:", error);
      addToast({
        title: t("imagen.enhanceError"),
        description: t("imagen.enhanceErrorDesc"),
        variant: "error",
        duration: 3000,
      });
      // Enhancement failed, restore original prompt
      setPrompt(originalPrompt);
    } finally {
      setIsEnhancing(false);
    }
  }, [prompt, isEnhanced, originalPrompt, selectedTemplateType, addToast, t]);

  // Typewriter strings for different modes
  const typewriterStrings = [
    t("imagen.slider.typed"),
    t("imagen.slider.badge"),
    t("imagen.slider.certificate"),
    t("imagen.slider.flashcard"),
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      className="min-h-auto md:min-h-[calc(100vh-120px)] w-full overflow-x-hidden flex flex-col justify-center"
    >
      {/* Main Content Layout */}
      <div className="flex flex-col h-auto justify-between mt-4 sm:mt-8">
        {!isMobile &&
          !isGenerating &&
          !showPreview &&
          topImages?.data &&
          topImages.data.length > 0 && (
            <div className="max-w-[70vw] mx-auto overflow-x-auto scrollbar-hide relative mb-8">
              <div className="relative">
                <div
                  id="top-images-carousel"
                  className="flex space-x-4 min-w-max overflow-x-auto scrollbar-hide featured-carousel"
                  style={{ scrollBehavior: "smooth" }}
                >
                  {[
                    ...topImages.data,
                    ...topImages.data,
                    ...topImages.data,
                  ].map((image, index) => (
                    <motion.div
                      key={`${image.id}-${index}`}
                      className="flex-shrink-0 w-28 aspect-[11/16] cursor-pointer group rounded-lg relative"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => setSelectedImage(image)}
                    >
                      <img
                        src={image.images?.[0]?.imageUrl || ""}
                        alt={image.title || "Generated image"}
                        className="w-full h-full object-contain transition-transform duration-300"
                      />
                      {/* Magnifier icon on hover */}
                      <div className="absolute inset-0 flex items-center justify-center bg-black/10 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="w-8 h-8 text-white drop-shadow-lg"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={2}
                        >
                          <circle
                            cx="11"
                            cy="11"
                            r="7"
                            stroke="currentColor"
                            strokeWidth="2"
                            fill="none"
                          />
                          <line
                            x1="16.5"
                            y1="16.5"
                            x2="21"
                            y2="21"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                          />
                        </svg>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>
          )}

        <div className="flex flex-col items-center">
          {/* Create a responsive layout container that centers content */}
          <div className="w-full">
            {/* Content Area with Image Preview and Input */}
            <div className="flex flex-col lg:flex-row gap-4 items-center">
              {/* Input and Controls Area */}
              <motion.div
                layout
                className={`flex-1 flex flex-col lg:flex-row items-center justify-center relative`}
              >
                <TemplateTypeSelector
                  selectedTemplateType={selectedTemplateType}
                  onTemplateTypeChange={handleTemplateTypeChange}
                />
                <AnimatePresence mode="wait">
                  {/* Loading State - Enhanced with better animations */}
                  {isGenerating && <ThinkingBubble />}

                  {/* Creation Mode - Enhanced SVG Background with Input */}
                  {!isGenerating && !showPreview && (
                    <CustomBubble isEnhancing={isEnhancing}>
                      {/* Input Section with Typewriter and Enhanced Button */}
                      <motion.div
                        className="relative"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.7, duration: 0.6 }}
                      >
                        <CustomPromptInput
                          value={prompt}
                          onChange={setPrompt}
                          highlightedWords={highlightedWords.map(
                            convertToHighlightedWord,
                          )}
                          placeholder={t("imagen.promptPlaceholder")}
                          disabled={isGenerating}
                          maxLength={1200}
                          dataTestId="imagen-prompt-input"
                          typewriterStrings={typewriterStrings}
                          isEnhanced={isEnhanced}
                        />
                      </motion.div>

                      {/* Enhanced Button */}
                      <motion.div
                        className="flex justify-center items-center gap-3 mt-6"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.9, duration: 0.6 }}
                      >
                        <EnhanceButton
                          isEnhanced={isEnhanced}
                          isEnhancing={isEnhancing}
                          onEnhanceClick={handleEnhanceClick}
                          disabled={isGenerating}
                        />

                        <motion.div
                          className="text-xs bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm px-3 py-3 rounded-full text-gray-500 shadow-lg border border-gray-200/50 dark:border-gray-700/50"
                          initial={{ opacity: 0, scale: 0.8 }}
                          animate={{ opacity: 1, scale: 1 }}
                          transition={{ delay: 1, duration: 0.3 }}
                        >
                          {prompt.length}/1200
                        </motion.div>

                        <GenerateIcon
                          isGenerating={isGenerating}
                          hasPrompt={hasPrompt}
                          onGenerate={handleGenerateImages}
                        />
                      </motion.div>
                    </CustomBubble>
                  )}
                  {!isGenerating && showPreview && (
                    <CustomBubble isEnhancing={isEnhancing}>
                      {/* Input Section with Typewriter and Enhanced Button */}
                      <motion.div
                        className="relative"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.7, duration: 0.6 }}
                      >
                        <CustomPromptInput
                          value={prompt}
                          onChange={setPrompt}
                          highlightedWords={highlightedWords.map(
                            convertToHighlightedWord,
                          )}
                          placeholder={t("imagen.promptPlaceholder")}
                          disabled={isGenerating}
                          maxLength={1200}
                          dataTestId="imagen-prompt-input"
                          typewriterStrings={typewriterStrings}
                          isEnhanced={isEnhanced}
                        />
                      </motion.div>

                      {/* Enhanced Button */}
                      <motion.div
                        className="flex justify-center items-center gap-3 mt-6"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.9, duration: 0.6 }}
                      >
                        <EnhanceButton
                          isEnhanced={isEnhanced}
                          isEnhancing={isEnhancing}
                          onEnhanceClick={handleEnhanceClick}
                          disabled={isGenerating}
                        />

                        <motion.div
                          className="text-xs bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm px-3 py-3 rounded-full text-gray-500 shadow-lg border border-gray-200/50 dark:border-gray-700/50"
                          initial={{ opacity: 0, scale: 0.8 }}
                          animate={{ opacity: 1, scale: 1 }}
                          transition={{ delay: 1, duration: 0.3 }}
                        >
                          {prompt.length}/1200
                        </motion.div>

                        <GenerateIcon
                          isGenerating={isGenerating}
                          hasPrompt={hasPrompt}
                          onGenerate={handleGenerateImages}
                        />
                      </motion.div>
                    </CustomBubble>
                  )}
                </AnimatePresence>
              </motion.div>
              {/* Image Preview Section */}
              <AnimatePresence>
                {showPreview &&
                  generatedImages?.images &&
                  generatedImages.images[0]?.imageUrl && (
                    <motion.div
                      layout
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.9 }}
                      transition={{ duration: 0.5, ease: "easeOut" }}
                      className="w-full md:w-2/5 lg:w-1/3 flex-shrink-0 mb-4 md:mb-0 self-center"
                      style={{ zIndex: 10 }}
                    >
                      <motion.div
                        className="bg-white/50 dark:bg-gray-800/50 backdrop-blur-xl rounded-3xl p-4 shadow-2xl border border-white/40 dark:border-gray-700/40"
                        whileHover={{ scale: 1.02 }}
                      >
                        <motion.div
                          className="relative aspect-[11/16] rounded-2xl overflow-hidden cursor-pointer group shadow-md"
                          onClick={() => setSelectedImage(generatedImages)}
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.98 }}
                          transition={{ duration: 0.3 }}
                        >
                          <img
                            src={generatedImages.images[0].imageUrl}
                            alt="Generated image preview"
                            className="w-full h-full object-cover"
                          />
                          <motion.div
                            className="absolute inset-0 flex items-center justify-center"
                            initial={{ opacity: 0 }}
                            whileHover={{ opacity: 1 }}
                            transition={{ duration: 0.3 }}
                          >
                            <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm rounded-full p-3 shadow-lg">
                              <Image className="w-6 h-6 text-gray-600 dark:text-gray-300" />
                            </div>
                          </motion.div>
                        </motion.div>
                      </motion.div>
                    </motion.div>
                  )}
              </AnimatePresence>
            </div>
          </div>
        </div>

        {/* Gallery navigation arrow - added to the bottom of the page */}
        <div className="flex flex-col items-center justify-center mt-2">
          <div
            className="flex items-center gap-2 text-gray-500 dark:text-gray-400 font-medium text-lg select-none cursor-pointer hover:text-gray-700 dark:hover:text-gray-300 transition-colors duration-200"
            onClick={onScrollToGallery}
          >
            <ChevronDown className="w-6 h-6 animate-pulse" />
            {t("imagen.myLibrary")}
            <ChevronDown className="w-6 h-6 animate-pulse" />
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default CreateImage;
